/**
 * Tip.jsx — instant-reveal tooltip (0 ms delay).
 *
 * Purpose-built for icon-only header controls (theme toggle, language
 * picker, "Other Modules" button, account link) where the browser's
 * default `title=` attribute has a 500-1500 ms delay that makes the
 * strip feel unresponsive.
 *
 * iter268e — PORTAL variant. The previous CSS-only implementation
 * worked, BUT the top-bar container in `GoogleTranslateBar.jsx`
 * needs `overflow-x-auto` for the narrow-phone scroll behaviour, and
 * that overflow clips any descendant absolutely-positioned tooltip
 * label. This version measures the trigger, listens to mouseenter /
 * mouseleave / focus / blur, and renders the label via
 * `createPortal(document.body)` so it can never be clipped.
 *
 * iter268f — VIEWPORT CLAMP. For controls on the far edges of the
 * top bar (e.g. the theme toggle at the right), a naïve
 * translateX(-50%) would push the label off-screen. After paint we
 * measure the label and clamp its left position within
 * [8, viewportWidth-8], flipping to plain-left / plain-right anchors
 * when centring would overflow.
 *
 * Usage stays IDENTICAL:
 *   <Tip label="Toggle dark mode">
 *     <button aria-label="Toggle dark mode">…</button>
 *   </Tip>
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const EDGE = 8; // px gutter from viewport edge

export default function Tip({ label, side = "bottom", children }) {
  const wrapRef  = useRef(null);
  const labelRef = useRef(null);
  const [visible, setVisible] = useState(false);
  // `anchor` describes how the label is anchored horizontally:
  //   "center"  → translateX(-50%)
  //   "left"    → no translate  (align to left edge of viewport gutter)
  //   "right"   → translateX(-100%) (align to right edge)
  const [pos, setPos] = useState({ top: 0, left: 0, anchor: "center" });

  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const centerX = r.left + r.width / 2;
    const gap = 8;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
    // Estimate label width from ref (may be null on the first paint).
    const labelW = labelRef.current?.offsetWidth || 160;

    let left = centerX;
    let anchor = "center";
    const leftEdgeIfCentered  = centerX - labelW / 2;
    const rightEdgeIfCentered = centerX + labelW / 2;
    if (leftEdgeIfCentered < EDGE) {
      // Would spill off the LEFT — anchor to viewport left gutter.
      left = EDGE;
      anchor = "left";
    } else if (rightEdgeIfCentered > vw - EDGE) {
      // Would spill off the RIGHT — anchor to viewport right gutter.
      left = vw - EDGE;
      anchor = "right";
    }
    setPos({
      top:  side === "bottom" ? r.bottom + gap : r.top - gap,
      left,
      anchor,
    });
  }, [side]);

  const show = useCallback(() => { measure(); setVisible(true); }, [measure]);
  const hide = useCallback(() => setVisible(false), []);

  // After the label paints once, re-measure so we can clamp on its
  // actual width (not the 160px estimate). Also re-measure on scroll
  // and resize while visible.
  useLayoutEffect(() => {
    if (!visible) return;
    measure();               // second pass with real label width
    const opts = { passive: true };
    window.addEventListener("scroll", measure, opts);
    window.addEventListener("resize", measure, opts);
    return () => {
      window.removeEventListener("scroll", measure, opts);
      window.removeEventListener("resize", measure, opts);
    };
  }, [visible, measure]);

  // Auto-hide when a click bubbles anywhere (opens a dropdown, etc.).
  useEffect(() => {
    if (!visible) return;
    const onClick = () => setVisible(false);
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [visible]);

  if (!label) return children;

  const transform = pos.anchor === "center"
    ? (side === "bottom" ? "translateX(-50%)" : "translate(-50%, -100%)")
    : pos.anchor === "left"
      ? (side === "bottom" ? "none" : "translateY(-100%)")
      : /* right */
        (side === "bottom" ? "translateX(-100%)" : "translate(-100%, -100%)");

  return (
    <>
      <span
        ref={wrapRef}
        className="hp-tip inline-flex"
        data-testid={`tip-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onPointerEnter={show}
        onPointerLeave={hide}
        onFocusCapture={show}
        onBlurCapture={hide}
      >
        {children}
      </span>

      {visible && typeof document !== "undefined" && createPortal(
        <span
          ref={labelRef}
          role="tooltip"
          aria-hidden="true"
          data-tip-portal="true"
          style={{
            position:  "fixed",
            top:       `${pos.top}px`,
            left:      `${pos.left}px`,
            maxWidth:  "calc(100vw - 16px)",
            transform,
            zIndex:    2147483000,      // above everything, incl. dropdowns
          }}
          className="hp-tip__label pointer-events-none whitespace-normal break-words rounded-md bg-[#121614] text-[#FAFAF9] text-xs font-medium px-2 py-1 shadow-lg text-center"
        >
          {label}
        </span>,
        document.body,
      )}
    </>
  );
}

